import Head from 'next/head'
import React, { CSSProperties, useContext } from 'react'

import { useCopy } from '@mondough/copy'
import { useLocale } from '@mondough/locale'
import { SkipLink } from '@mondough/monzo-ui'

import { ReferralContext, TabContext } from '../../providers'
import desktopBg from '../../public/images/desktop-bg.png'
import mobileBg from '../../public/images/mobile-bg.png'
import lifestyleGeneric from '../../public/images/monzo-campaign.webp'
import lifestyleReferral from '../../public/images/referral.png'
import wideBg from '../../public/images/wide-bg.png'
import { NUMBER_OF_CUSTOMERS_MILLIONS, getCDNSrc } from '../../utils'
import styles from './Layout.module.scss'
import { Footer, Header, MultipleBlockBanner, SingleBlockBanner } from '..'

const Layout = ({
  children,
}: {
  children: React.ReactNode
  renameFlag?: boolean
}) => {
  const { referralData, defaultReferralText } = useContext(ReferralContext)
  const { tab } = useContext(TabContext)
  const { fullLocale } = useLocale()
  const c = useCopy()
  const displayReferral = referralData !== null

  // Legal entity/links follow the Split's country when viewing one, else the
  // viewer's resolved locale (e.g. on signup/home where there's no tab).
  const footerCountry = tab?.country ?? fullLocale.country.alpha3

  const headTitle = c.string({
    key: 'app_tabs_monzo_me.common.app_name',
    message: 'Monzo Split',
  })
  const description = c.string({
    key: 'app_tabs_monzo_me.common.tagline.guest_description',
    message:
      'Join Monzo Split as a guest to add, settle and track group costs.',
  })
  const url = c.string({
    key: 'app_tabs_monzo_me.default.page.url',
    message: 'https://split.monzo.me',
  })

  return (
    <div className={styles.app}>
      <Head>
        <title>{headTitle}</title>
        <meta
          name="viewport"
          content="width=device-width, height=device-height, initial-scale=1, maximum-scale=5"
        />
        <meta name="robots" content="noindex" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={headTitle} />
        <meta property="og:url" content={url} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/og-images/split-og.png" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@monzo" />
        <meta name="twitter:title" content={headTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/og-images/split-og.png" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="description" content={description} />
        <meta name="theme-color" content="#112231" />
      </Head>
      <SkipLink target="main" />
      <Header />
      <main className={styles.main} id="main">
        <div className={styles['desktop-row-container']}>
          <section
            className={styles['left-side-content']}
            style={
              {
                '--mobile-bg-url': `url(${getCDNSrc(mobileBg)})`,
                '--desktop-bg-url': `url(${getCDNSrc(desktopBg)})`,
                '--wide-bg-url': `url(${getCDNSrc(wideBg)})`,
              } as CSSProperties
            }
          >
            <div className={styles.content}>{children}</div>
          </section>
          <aside className={styles['right-side-content']}>
            <SingleBlockBanner
              title={
                displayReferral
                  ? referralData?.title
                  : c.string({
                      key: 'app_tabs_monzo_me.referral.default.title',
                      message:
                        'Join {{ numberOfMillionCustomers }} million people banking with Monzo',
                      variables: {
                        numberOfMillionCustomers: NUMBER_OF_CUSTOMERS_MILLIONS,
                      },
                    })
              }
              text={
                displayReferral
                  ? referralData?.description
                  : defaultReferralText
              }
              imageSrc={displayReferral ? lifestyleReferral : lifestyleGeneric}
              buttonText={
                displayReferral
                  ? c.string({
                      key: 'app_tabs_monzo_me.referral.claim.button',
                      message: 'Claim referral',
                    })
                  : c.string({
                      key: 'app_tabs_monzo_me.common.cta.get_free_account',
                      message: 'Get a free bank account',
                    })
              }
              buttonHref={
                displayReferral
                  ? referralData?.link
                  : 'https://monzo.com/sign-up/?utm_source=tabsmonzome&utm_medium=website'
              }
            />
          </aside>
        </div>
        <MultipleBlockBanner />
      </main>
      <Footer forCountry={footerCountry} />
    </div>
  )
}

export default Layout
